import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { stringifyError } from 'shared/functions';
import { selectSettings } from 'shared/selectors';

import { GenericContextType } from 'types/generic-context';

import { GenericContext } from '../constants';

export const useData = <R>(fn: () => undefined | Promise<R>) => {
  const [data, setData] = useState<R>(null);
  const [loading, setLoading] = useState<boolean>(null);
  const [error, setError] = useState<string>(null);

  const loadData = async () => {
    try {
      setLoading(true);

      const res = typeof fn === 'function' ? await fn() : undefined;

      setLoading(false);

      setData(res);
    } catch (error) {
      setLoading(false);

      setError(stringifyError(error));
    }
  };

  useEffect(
    () => {
      loadData();
    },
    // fire only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return [data, loading, error, loadData] as const;
};

export const useSocketEvent = <T>(eventKey: string, eventHandler: (data: T) => void) => {
  const { socket } = useContext(GenericContext) as GenericContextType;

  useEffect(() => {
    socket.on(eventKey, eventHandler);

    return () => {
      socket.off(eventKey, eventHandler);
    };
  }, [socket, eventKey, eventHandler]);
};

export const useSettings = () => useSelector(selectSettings);
